import React, { memo, useLayoutEffect, useMemo, useRef } from 'react';
import DOMPurify from 'dompurify';
import { prepareEmailHtmlForDisplay } from '../utils/mailPreferences';
import { useTheme } from '../context/ThemeContext';

let colorProbeCtx = null;

function parseCssColor(input) {
  if (!input) return null;
  const s = String(input).trim().toLowerCase();
  if (!s || s === 'transparent' || s === 'rgba(0, 0, 0, 0)') return null;

  const rgb = s.match(/rgba?\(\s*([\d.]+)\s*,\s*([\d.]+)\s*,\s*([\d.]+)(?:\s*,\s*([\d.]+))?/i);
  if (rgb) {
    const alpha = rgb[4] != null ? Number(rgb[4]) : 1;
    if (alpha <= 0.08) return null;
    return { r: Number(rgb[1]), g: Number(rgb[2]), b: Number(rgb[3]) };
  }

  const hex = s.match(/^#([0-9a-f]{3}|[0-9a-f]{6})$/i);
  if (hex) {
    let h = hex[1];
    if (h.length === 3) h = h.split('').map((c) => c + c).join('');
    return {
      r: parseInt(h.slice(0, 2), 16),
      g: parseInt(h.slice(2, 4), 16),
      b: parseInt(h.slice(4, 6), 16),
    };
  }

  try {
    if (!colorProbeCtx) {
      colorProbeCtx = document.createElement('canvas').getContext('2d');
    }
    // Ungültige Werte dürfen nicht still auf die Probe-Farbe fallen
    colorProbeCtx.fillStyle = '#000001';
    colorProbeCtx.fillStyle = s;
    const resolved = String(colorProbeCtx.fillStyle || '').toLowerCase();
    if (!resolved || resolved === '#000001') return null;
    return parseCssColor(resolved);
  } catch (_) {
    return null;
  }
}

function relativeLuminance({ r, g, b }) {
  const toLin = (v) => {
    const c = v / 255;
    return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
  };
  return 0.2126 * toLin(r) + 0.7152 * toLin(g) + 0.0722 * toLin(b);
}

function contrastRatio(fg, bg) {
  const a = relativeLuminance(fg);
  const b = relativeLuminance(bg);
  const lighter = Math.max(a, b);
  const darker = Math.min(a, b);
  return (lighter + 0.05) / (darker + 0.05);
}

const NAMED_BG = {
  white: { r: 255, g: 255, b: 255 },
  black: { r: 0, g: 0, b: 0 },
  silver: { r: 192, g: 192, b: 192 },
  gray: { r: 128, g: 128, b: 128 },
  grey: { r: 128, g: 128, b: 128 },
  lightgray: { r: 211, g: 211, b: 211 },
  lightgrey: { r: 211, g: 211, b: 211 },
  whitesmoke: { r: 245, g: 245, b: 245 },
};

function readBgcolorAttr(el) {
  const raw = el.getAttribute?.('bgcolor');
  if (!raw) return null;
  const value = String(raw).trim();
  if (!value) return null;
  if (value.startsWith('#') || /^rgba?\(/i.test(value)) return parseCssColor(value);
  if (/^[0-9a-f]{3}$|^[0-9a-f]{6}$/i.test(value)) return parseCssColor(`#${value}`);
  const named = NAMED_BG[value.toLowerCase().replace(/\s+/g, '')];
  if (named) return { ...named };
  return parseCssColor(value);
}

/** Inline style="background(-color): …" falls computed transparent bleibt */
function readInlineBackground(el) {
  const style = el.getAttribute?.('style');
  if (!style) return null;
  const match = String(style).match(
    /(?:^|;)\s*background(?:-color)?\s*:\s*([^;]+)/i
  );
  if (!match) return null;
  const raw = match[1].trim();
  // background: url(...) #fff → letzte Farbangabe nehmen
  const colorToken = raw.match(/#(?:[0-9a-f]{3}|[0-9a-f]{6})\b|rgba?\([^)]+\)|\b(?:white|black|transparent|[a-z]+)\b/gi);
  if (!colorToken || !colorToken.length) return null;
  for (let i = colorToken.length - 1; i >= 0; i -= 1) {
    const parsed = parseCssColor(colorToken[i]);
    if (parsed) return parsed;
  }
  return null;
}

function effectiveBackground(el, root) {
  let node = el;
  while (node) {
    if (node instanceof HTMLElement) {
      const fromAttr = readBgcolorAttr(node);
      if (fromAttr) return { color: fromAttr, sourceEl: node };
      // Computed vor Inline: App-CSS (z. B. Dark-Mode-Zitat) schlägt Absender-Inline
      const fromStyle = parseCssColor(window.getComputedStyle(node).backgroundColor);
      if (fromStyle) return { color: fromStyle, sourceEl: node };
      const fromInline = readInlineBackground(node);
      if (fromInline) return { color: fromInline, sourceEl: node };
    }
    if (node === root) break;
    node = node.parentElement;
  }

  // Invert-Modus: Frame ist weißes „Papier“ vor dem Filter
  if (root?.classList?.contains('email-html-invert')) {
    return { color: { r: 255, g: 255, b: 255 }, sourceEl: root };
  }

  if (document.documentElement.classList.contains('dark')) {
    return { color: { r: 23, g: 23, b: 23 }, sourceEl: null };
  }
  return { color: { r: 255, g: 255, b: 255 }, sourceEl: null };
}

function hasOwnText(el) {
  // Nur direkte Textknoten — sonst überschreibt color:!important auf td/div
  // die Inline-Farben von Links (z. B. BauOS-Rot im Footer).
  for (const node of el.childNodes) {
    if (node.nodeType === Node.TEXT_NODE && node.textContent && node.textContent.trim()) {
      return true;
    }
  }
  if (el.matches?.('font, a, span, strong, em, b, i, label')) {
    return String(el.textContent || '').trim().length > 0;
  }
  return false;
}

function setTextColor(el, color) {
  // !important schlägt Absender-CSS (oft aus prefers-color-scheme: dark)
  el.style.setProperty('color', color, 'important');
}

function hasExplicitInlineColor(el) {
  const style = el.getAttribute?.('style');
  if (!style) return false;
  return /(?:^|;)\s*color\s*:/i.test(style);
}

/** Unsere Antwortzitate (helle Inline-Styles für externe Clients) → Dark Mode. */
function adaptAppQuotesForDarkMode(root) {
  if (!root || !document.documentElement.classList.contains('dark')) return;
  if (root.classList.contains('email-html-invert')) return;

  const quotes = root.querySelectorAll('.email-quote, .email-quote-box');
  quotes.forEach((el) => {
    if (!(el instanceof HTMLElement)) return;
    const style = el.getAttribute('style') || '';
    const isCardQuote =
      el.classList.contains('email-quote-box') ||
      /background(?:-color)?\s*:\s*#fafafa/i.test(style) ||
      /background(?:-color)?\s*:\s*#FAFAFA/i.test(style) ||
      /border-left\s*:\s*3px/i.test(style);

    if (!isCardQuote) return;

    el.style.setProperty('background', '#262626', 'important');
    el.style.setProperty('background-color', '#262626', 'important');
    el.style.setProperty('color', '#e5e5e5', 'important');
    el.style.setProperty('border-left-color', '#737373', 'important');

    el.querySelectorAll('p, span, div, strong, em, b, i').forEach((child) => {
      if (!(child instanceof HTMLElement)) return;
      const childStyle = child.getAttribute('style') || '';
      const isMeta =
        child.classList.contains('email-quote-meta') ||
        /color\s*:\s*#737373/i.test(childStyle) ||
        /color\s*:\s*#6b7280/i.test(childStyle);
      if (isMeta) {
        child.style.setProperty('color', '#a3a3a3', 'important');
      } else {
        child.style.setProperty('color', '#e5e5e5', 'important');
      }
    });
  });
}

/**
 * Schlechten Kontrast in HTML-Mails automatisch korrigieren:
 * - helle Schrift auf hellem Grund → dunkel
 * - graue/dunkle Schrift auf dunklem/grauem/schwarzem Grund → hell
 *
 * Auch im Invert-Modus: zuerst lesbares Hell-Layout herstellen,
 * danach greift der CSS-Filter.
 */
function fixEmailTextContrast(root) {
  if (!root) return;

  const nodes = root.querySelectorAll('font, a, span, p, li, h1, h2, h3, h4, h5, h6, td, th, div, strong, em, b, i, label');
  nodes.forEach((el) => {
    if (!(el instanceof HTMLElement) || !hasOwnText(el)) return;

    const cs = window.getComputedStyle(el);
    if (cs.visibility === 'hidden' || cs.display === 'none') return;

    const fg = parseCssColor(cs.color);
    if (!fg) return;

    const { color: bg, sourceEl } = effectiveBackground(el, root);
    const bgLum = relativeLuminance(bg);
    const ratio = contrastRatio(fg, bg);

    // Explizite Link-/Akzentfarben (z. B. #E31E24) nicht überschreiben,
    // solange der Kontrast akzeptabel ist.
    if (hasExplicitInlineColor(el) && ratio >= 3.0) return;

    if (bgLum >= 0.62) {
      if (ratio < 3.4) setTextColor(el, '#171717');
      return;
    }

    if (bgLum <= 0.48) {
      // Nur schützen, wenn der dunkle Hintergrund nicht explizit am Element/Vorfahren
      // liegt (z. B. Dark-Mode-Fallback), aber dazwischen eine helle Zelle sitzt.
      // Bei Navy-Callouts in weißem Wrapper darf der Wrapper nicht den Text umbiegen.
      let ancestorLight = false;
      if (!sourceEl) {
        let walk = el.parentElement;
        while (walk) {
          const parentBg = readBgcolorAttr(walk) || readInlineBackground(walk);
          if (parentBg && relativeLuminance(parentBg) >= 0.62) {
            ancestorLight = true;
            break;
          }
          if (walk === root) break;
          walk = walk.parentElement;
        }
      }
      if (ancestorLight) {
        if (ratio < 3.4) setTextColor(el, '#171717');
        return;
      }
      if (ratio < 3.4) setTextColor(el, '#f5f5f5');
      return;
    }

    if (ratio < 3.2) {
      setTextColor(el, bgLum >= 0.5 ? '#171717' : '#f5f5f5');
    }
  });
}

function fluidizeEmailForMobile(root) {
  if (!root || typeof window === 'undefined') return;
  if (!window.matchMedia('(max-width: 640px)').matches) return;

  const softenWidth = (el) => {
    if (!(el instanceof HTMLElement)) return;
    const attrW = el.getAttribute('width');
    if (attrW && /^\d+%?$/.test(attrW)) {
      if (!attrW.endsWith('%') && Number(attrW) > 280) {
        el.setAttribute('width', '100%');
      }
    }
    const styleW = el.style.width;
    if (styleW) {
      if (/px$/i.test(styleW)) {
        const px = parseInt(styleW, 10);
        if (Number.isFinite(px) && px > 280) el.style.setProperty('width', '100%', 'important');
      } else if (/^\d+$/.test(styleW) && Number(styleW) > 280) {
        el.style.setProperty('width', '100%', 'important');
      }
    }
    const minW = el.style.minWidth;
    if (minW && /px$/i.test(minW)) {
      const px = parseInt(minW, 10);
      if (Number.isFinite(px) && px > 280) el.style.setProperty('min-width', '0', 'important');
    }
    el.style.setProperty('max-width', '100%', 'important');
  };

  root.style.setProperty('max-width', '100%', 'important');
  root.style.setProperty('width', '100%', 'important');
  root.style.setProperty('overflow-wrap', 'anywhere', 'important');
  root.style.setProperty('word-break', 'break-word', 'important');

  root.querySelectorAll('img, video').forEach((el) => {
    if (!(el instanceof HTMLElement)) return;
    el.style.setProperty('max-width', '100%', 'important');
    el.style.setProperty('height', 'auto', 'important');
  });

  root.querySelectorAll('table, td, th, div, section, article, center, p, span, h1, h2, h3, h4, h5, h6').forEach(softenWidth);

  root.querySelectorAll('[style*="white-space"]').forEach((el) => {
    if (!(el instanceof HTMLElement)) return;
    if (/nowrap/i.test(el.style.whiteSpace || '')) {
      el.style.setProperty('white-space', 'normal', 'important');
    }
  });

  root.querySelectorAll('table[nowrap], td[nowrap], th[nowrap]').forEach((el) => {
    el.removeAttribute('nowrap');
  });
}

const MOBILE_EMAIL_OVERRIDE_STYLE = `
@media (max-width: 640px) {
  .email-html-frame {
    width: 100% !important;
    max-width: 100% !important;
    overflow-x: visible !important;
    overflow-wrap: anywhere !important;
    word-break: break-word !important;
  }
  .email-html-frame * {
    max-width: 100% !important;
    box-sizing: border-box !important;
  }
  .email-html-frame table {
    width: 100% !important;
  }
  .email-html-frame img,
  .email-html-frame video {
    max-width: 100% !important;
    height: auto !important;
  }
  .email-html-frame pre,
  .email-html-frame code {
    white-space: pre-wrap !important;
    word-break: break-word !important;
  }
}
`;

/**
 * Rendert eingehende HTML-Mails inkl. gescopter Absender-Styles
 * (Klassen wie Tipico .fc1), ohne die App-CSS zu überschreiben.
 */
const EmailHtmlBody = memo(({ html, className = '', allowExternalImages = false }) => {
  const rootRef = useRef(null);
  const { isDarkMode } = useTheme();

  const safeHtml = useMemo(() => {
    if (!html) return '';
    const { html: prepared, purifyConfig } = prepareEmailHtmlForDisplay(html, {
      allowExternalImages,
    });
    return DOMPurify.sanitize(prepared, purifyConfig);
  }, [html, allowExternalImages]);

  useLayoutEffect(() => {
    if (!rootRef.current || !safeHtml) return undefined;
    // Doppeltes rAF: scoped <style> aus innerHTML ist erst danach zuverlässig aktiv
    let innerId = 0;
    const outerId = window.requestAnimationFrame(() => {
      innerId = window.requestAnimationFrame(() => {
        fluidizeEmailForMobile(rootRef.current);
        adaptAppQuotesForDarkMode(rootRef.current);
        fixEmailTextContrast(rootRef.current);
      });
    });
    const onResize = () => fluidizeEmailForMobile(rootRef.current);
    window.addEventListener('resize', onResize);
    return () => {
      window.cancelAnimationFrame(outerId);
      window.cancelAnimationFrame(innerId);
      window.removeEventListener('resize', onResize);
    };
  }, [safeHtml, className, isDarkMode]);

  if (!html) return null;

  return (
    <>
      <style data-odium-mobile-email="1">{MOBILE_EMAIL_OVERRIDE_STYLE}</style>
      <div
        key={isDarkMode ? 'email-html-dark' : 'email-html-light'}
        ref={rootRef}
        className={className}
        dangerouslySetInnerHTML={{ __html: safeHtml }}
      />
    </>
  );
});

EmailHtmlBody.displayName = 'EmailHtmlBody';

export default EmailHtmlBody;
